import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { images } from '../../components/shared/assets';
import SmartNavigation from '../../components/Navigation/SmartNavigation';
import InstagramSection from '../../components/BrandHomePage/InstagramSection';
import Footer from '../../components/Footer/Footer';
import { HandDrawnButton } from '../../components/shared/HandDrawnButton/HandDrawnButton';
import './ContactPage.css';
import emailjs from '@emailjs/browser';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  country: string;
  howDidYouHear: string;
  property: string;
  additionalInfo: string;
}

type ContactPageProps = {
  full?: Boolean;
};

const ContactPage: React.FC<ContactPageProps> = ({ full = true }) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    country: 'USA',
    howDidYouHear: 'Instagram',
    property: 'Awasi Atacama',
    additionalInfo: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRadioChange = (property: string) => {
    setFormData(prev => ({ ...prev, property }));
  };

  const checkFormData = () => {
    let error = '';
    if (formData.firstName === '') {
      error = `${t('forms.contact.errors.firstName')}\n`
    }
    if (formData.lastName === '') {
      error += `${t('forms.contact.errors.lastName')}\n`;
    }
    if (formData.email === '') {
      error += `${t('forms.contact.errors.email')}\n`;
    }
    if (formData.phoneNumber === '') {
      error += `${t('forms.contact.errors.phoneNumber')}\n`;
    }
    if (error) {
      alert(error);
      return false;
    }
    return true;
  };

  /**
   * Handle form submission by sending an email using EmailJS.
   * @param {React.FormEvent} e - Form submission event.
   */
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    const serviceID = 'awasi_ses';
    const templateID = 'template_6x2zoeb';
    const publicKey = 'De7GlfGR3o6Abv2R7';

    if (formRef.current && checkFormData()) {
      emailjs.sendForm(serviceID, templateID, formRef.current, publicKey)
        .then(() => {
          console.log('Correo enviado correctamente');
          alert(t('forms.contact.successMessage'));
          setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phoneNumber: '',
            country: 'USA',
            howDidYouHear: 'Instagram',
            property: 'Awasi Atacama',
            additionalInfo: ''
          });
          setLoading(false);
        }, (error) => {
          console.log('Error al enviar el correo:', error.text);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  const tollFreeNumbers = [
    { country: t('pages/contact:usa'), number: '1-888-880-3219' },
    { country: t('pages/contact:australia'), number: '1-800-768-365' },
    { country: t('pages/contact:switzerland'), number: '0-800-563-267' },
    { country: t('pages/contact:germany'), number: '0-800-180-2590' },
    { country: t('pages/contact:brazil'), number: '0-800-892-1347' },
    { country: t('pages/contact:france'), number: '0-800-915828' },
    { country: t('pages/contact:uk'), number: '0-808-101-6778' }
  ];

  const hotelContacts: Array<{
    name: string;
    address: string;
    phone: string;
    whatsapp?: string;
    website: string;
  }> = [
      {
        name: 'Awasi Atacama',
        address: 'Tocopilla 551, San Pedro de Atacama, Antofagasta, Chile',
        phone: '+56222339641',
        website: 'Website'
      },
      {
        name: 'Awasi Patagonia',
        address: 'Estancia Tercera Barranca, Torres del Paine, Magallanes y la Antártica Chilena, Chile',
        phone: '+56222339641',
        website: 'Website'
      },
      {
        name: 'Awasi Iguazú',
        address: 'Selva Yriapú S/N, Puerto Iguazú, Misiones, N3370GOA Puerto Iguazú, Misiones, Argentina',
        phone: '+56222339641',
        website: 'Website'
      },
      {
        name: 'Awasi Santa Catarina',
        address: 'Rua Elpídeo Alves do Nascimento, 104 Governador Celso Ramos – SC',
        phone: '+ 55 48 3262-5040',
        whatsapp: '+ 55 48 3262-5000',
        website: 'Website'
      },
      {
        name: 'Awasi Mendoza',
        address: 'Agrelo, Lujan de Cuyo, Mendoza, Argentina',
        phone: '+54 9261 533 5203',
        whatsapp: '+54 9261 533 5205',
        website: 'Website'
      }
    ];


  return (
    <div className="contact-page" id="contact-page">

      {full &&
        <>
          <title>{t('meta:contact.title')}</title>
          <meta name="description" content={t('meta:contact.description')} />
          <meta property="og:title" content={t('meta:contact.title')} />
          <meta property="og:description" content={t('meta:contact.description')} />

          {/* Navigation */}
          <SmartNavigation forceVariant="light" />
        </>
      }

      {/* Contact Us Section */}
      <section className="contact-hero" style={{ backgroundImage: `url(${images.contactPageBg})` }}>
        <div className="contact-form-container">
          <div className="contact-form-card">
            <div className="contact-form-header">
              <h1>{t('forms.contact.title')}</h1>
            </div>

            <form
              noValidate
              className="contact-form-content"
              aria-label={t('accessibility.landmarks.form')}
              onSubmit={handleSubmit}
              ref={formRef}
            >
              <div className="form-grid">
                {/* First Name */}
                <div className="form-field">
                  <label htmlFor="firstName">{t('forms.contact.labels.firstName')}</label>
                  <input
                    required
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder={t('forms.contact.placeholders.firstName')}
                    aria-required="true"
                    aria-describedby="firstName-required"
                    autoComplete="given-name"
                  />
                  <span id="firstName-required" className="sr-only">{t('accessibility.forms.required')}</span>
                </div>

                {/* Last Name */}
                <div className="form-field">
                  <label htmlFor="lastName">{t('forms.contact.labels.lastName')}</label>
                  <input
                    required
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder={t('forms.contact.placeholders.lastName')}
                  />
                </div>

                {/* Email */}
                <div className="form-field">
                  <label htmlFor="email">{t('forms.contact.labels.email')}</label>
                  <input
                    required
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t('forms.contact.placeholders.email')}
                    aria-required="true"
                    aria-describedby="email-required"
                    autoComplete="email"
                  />
                  <span id="email-required" className="sr-only">{t('accessibility.forms.required')}</span>
                </div>

                {/* Phone Number */}
                <div className="form-field">
                  <label htmlFor="phoneNumber">{t('forms.contact.labels.phoneNumber')}</label>
                  <input
                    required
                    type="tel"
                    id="phoneNumber"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    placeholder={t('forms.contact.placeholders.phoneNumber')}
                  />
                </div>

                {/* Country Dropdown */}
                <div className="form-field">
                  <label htmlFor="country">{t('forms.contact.labels.country')}</label>
                  <div className="select-wrapper">
                    <select
                      id="country"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="USA">{t('forms.contact.countries.usa')}</option>
                      <option value="UK">{t('forms.contact.countries.uk')}</option>
                      <option value="Canada">{t('forms.contact.countries.canada')}</option>
                      <option value="Australia">{t('forms.contact.countries.australia')}</option>
                      <option value="Other">{t('forms.contact.countries.other')}</option>
                    </select>
                    <svg className="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24">
                      <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                  </div>
                </div>

                {/* How did you hear Dropdown */}
                <div className="form-field">
                  <label htmlFor="howDidYouHear">{t('forms.contact.labels.howDidYouHear')}</label>
                  <div className="select-wrapper">
                    <select
                      id="howDidYouHear"
                      name="howDidYouHear"
                      value={formData.howDidYouHear}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="Instagram">{t('forms.contact.sources.instagram')}</option>
                      <option value="Google">{t('forms.contact.sources.google')}</option>
                      <option value="Friend">{t('forms.contact.sources.friend')}</option>
                      <option value="Press">{t('forms.contact.sources.press')}</option>
                      <option value="Other">{t('forms.contact.sources.other')}</option>
                    </select>
                    <svg className="dropdown-arrow" width="24" height="24" viewBox="0 0 24 24">
                      <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" fill="none" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Property Selection */}
              <div className="property-selection">
                <label className="section-label">{t('forms.contact.labels.selectProperty')}</label>
                <div className="radio-group">
                  {[
                    { name: 'Awasi Atacama', key: 'atacama' },
                    { name: 'Awasi Patagonia', key: 'patagonia' },
                    { name: 'Awasi Iguazú', key: 'iguazu' },
                    { name: 'Awasi Santa Catarina', key: 'santaCatarina' },
                    { name: 'Awasi Mendoza', key: 'mendoza' }
                  ].map((property) => (
                    <label key={property.key} className="radio-label">
                      <input
                        type="radio"
                        name="property"
                        value={property.name}
                        required
                        checked={formData.property === property.name}
                        onChange={() => handleRadioChange(property.name)}
                      />
                      <span className="radio-custom"></span>
                      <span className="radio-text">{t(`forms.contact.properties.${property.key}`)}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Additional Information */}
              <div className="form-field full-width">
                <label htmlFor="additionalInfo">{t('forms.contact.labels.additionalInfo')}</label>
                <textarea
                  id="additionalInfo"
                  name="additionalInfo"
                  value={formData.additionalInfo}
                  onChange={handleInputChange}
                  placeholder={t('forms.contact.placeholders.additionalInfo')}
                  rows={4}
                />
                <svg className="corner-icon" width="12" height="12" viewBox="0 0 12 12">
                  <path d="M0 12L12 0V12H0Z" fill="currentColor" />
                </svg>
              </div>

              <div className="contact-form-footer">
                {/* Submit Button */}
                <HandDrawnButton
                  text={loading ? t('common:loading.pleaseWait') : t('common:buttons.submit')}
                  variant="brand"
                  color="awasi"
                  animation="stroke-to-fill"
                  size="wide"
                  className="contact-form-submit-button"
                  disabled={loading}
                  type="submit"
                />
              </div>
            </form>
          </div>
        </div>
      </section>

      {full &&
        <>

          {/* Contact Information Section */}
          <section className="contact-info" style={{ paddingBlock: '200px' }}>
            <div className="container">
              {/* Reservations and Email */}
              <div className="contact-header">
                <div className="contact-row">
                  <h3>{t('pages/contact:reservations')}:</h3>
                  <p>+(56-2) 2233-9641</p>
                </div>
                <div className="contact-row">
                  <h3>{t('pages/contact:emailInquire')}:</h3>
                  <p>info@awasi.com</p>
                </div>
              </div>

              {/* <div className="divider"></div> */}

              {/* Toll Free Numbers */}
              <div className="toll-free-section">
                <h3>{t('pages/contact:tollFree')}:</h3>
                <div className="toll-free-grid">
                  {tollFreeNumbers.map((item) => (
                    <div key={item.country} className="toll-free-item">
                      <span className="country">{item.country}</span>
                      <span className="number">{item.number}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* <div className="divider"></div> */}

              {/* Hotel Contacts */}
              <div className="hotel-contacts">
                {hotelContacts.map((hotel) => (
                  <div key={hotel.name} className="hotel-item">
                    <div className="top-line"></div>
                    <h3>{hotel.name}</h3>
                    <div className="hotel-details">
                      <p>{hotel.address}</p>
                      <a href={`tel:${hotel.phone}`}>{hotel.phone}</a>
                      {hotel.whatsapp && (
                        <p>WhatsApp {hotel.whatsapp}</p>
                      )}
                      {/* <a href={`https://${hotel.website}`} className="website-link" target="_blank" rel="noopener noreferrer">{hotel.website}</a> */}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Instagram Section */}
          <div className="contact-instagram">
            <InstagramSection />
          </div>

          {/* Footer */}
          <Footer variant="default" />
        </>}
    </div>
  );
};

export default ContactPage;